import { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowsH, faBan, faCheckCircle, faGlobe,
} from '@fortawesome/pro-light-svg-icons'

import {
  getQueryParamsFromHash, parsePermittedQueryParams, sortArrayBy,
} from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, Tools,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useProviderPriceList from '@hooks/useProviderPriceList'
import useProviderPriceLists from '@hooks/useProviderPriceLists'
import useSelectEntities from '@hooks/useSelectEntities'
import useServices from '@hooks/useServices'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import type { ServiceModel } from '@models/types'

import ProviderPriceListListItem from '../ProviderPriceListListItem'

const getActiveTabBarKey = (params: { isHidden: boolean, isMaster: boolean }) => {
  const { isHidden, isMaster } = params

  if (isHidden) return 'archived'
  if (isMaster) return 'master'
  return 'active'
}

const buildPageFilterFields = (filteredServices: ServiceModel[]) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: sortArrayBy(Object.values(filteredServices), 'asc', 'title').map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { hidden, is_master } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden', 'is_master'])
  const isHidden = hidden === 'true'
  const isMaster = is_master === 'true'

  const providerPriceListsPayload = useProviderPriceLists({
    filters: {
      hidden: isHidden,
      is_master: isMaster,
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: { include_provider_price_list_provider: true },
  })

  const providerPriceListPayload = useProviderPriceList()
  const {
    callbacks: {
      createOrEditProviderPriceList,
      launchBulkUpdateProviderPriceLists,
    },
    urls: {
      archivedProviderPriceListsIndexUrl,
      masterProviderPriceListsIndexUrl,
      providerPriceListsIndexUrl,
    },
  } = providerPriceListPayload

  const { filteredServices } = useServices({ performHttpRequests: true })

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPriceLists,
    filteredProviderPriceListsCount,
    hasFilteredProviderPriceLists,
    loading,
  } = providerPriceListsPayload

  const unselectEntitiesPayload = useSelectEntities()
  const {
    callbacks: {
      deselectAll,
      getSelectableListItemProps,
    },
    hasMinimumSelected,
    selectedEntityIds,
  } = unselectEntitiesPayload

  return (
    <>
      <Helmet>
        <title>Price Lists | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={createOrEditProviderPriceList}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey={getActiveTabBarKey({ isHidden, isMaster })}
        tabBarItems={[
          {
            href: providerPriceListsIndexUrl,
            icon: faCheckCircle,
            key: 'active',
            title: 'Active',
          },
          {
            href: masterProviderPriceListsIndexUrl,
            icon: faGlobe,
            key: 'master',
            title: 'Master',
          },
          {
            href: archivedProviderPriceListsIndexUrl,
            icon: faBan,
            key: 'archived',
            title: 'Archived',
          },
        ]}
        title="Price Lists"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredProviderPriceLists && <ListBlankState />}

            {!loading && hasFilteredProviderPriceLists && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Price Lists">
                <Box flexDirection="column">
                  {filteredProviderPriceLists.map((providerPriceList) => {
                    const listItemProps = getSelectableListItemProps(providerPriceList.id)

                    return (
                      <ProviderPriceListListItem
                        key={providerPriceList.id}
                        providerPriceList={providerPriceList}
                        {...listItemProps}
                      />
                    )
                  })}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredProviderPriceListsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(filteredServices)}
              pageFilters={pageFilters}
              title="Filters"
            />

            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                disabled={!hasMinimumSelected}
                // @ts-expect-error TS2322
                ghost
                icon={<FontAwesomeIcon icon={faArrowsH} />}
                onClick={() => launchBulkUpdateProviderPriceLists({
                  // @ts-expect-error TS2353
                  callbacks: { deselectAll },
                  priceListIds: selectedEntityIds,
                })}
                size="medium"
              >
                Bulk Update
              </Button>
            </Tools>
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
